import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

// Hardcoded expected iter marker — MUST match the `iter_marker` value
// baked into `backend/server.py`'s /health/version endpoint for the
// commit paired with this frontend build. Bump both in the same
// commit whenever a perf/behaviour-critical backend change ships.
//
// Rationale: the recurring 504 storms on /api/partner/transactions
// have all traced to a deploy-sync mismatch (frontend deployed, backend
// stuck on an older revision or vice-versa). This constant lets a live
// user see IMMEDIATELY that the two halves are out of sync without
// having to open DevTools + curl /health/version.
export const EXPECTED_ITER_MARKER = "iter-96";

// Extract the numeric suffix from an iter marker string ("iter-62" -> 62).
// Returns NaN for anything unparseable so comparisons safely short-circuit.
function iterNum(s) {
    if (!s || typeof s !== "string") return NaN;
    const m = s.match(/iter-(\d+)/i);
    return m ? parseInt(m[1], 10) : NaN;
}

/**
 * Deploy Sync Banner
 *
 * Polls /api/health/version on mount + every 30s and compares the
 * backend's live `iter_marker` to `EXPECTED_ITER_MARKER` (the value
 * baked into this frontend bundle at build time).
 *
 *  • Backend < expected → BACKEND deploy is in flight. Yellow banner.
 *  • Backend > expected → FRONTEND is stale (CDN cache / bookmarked).
 *    Amber banner with a "Reload" CTA that force-refreshes ignoring
 *    the HTTP cache.
 *  • Match → silent (returns null).
 *
 * Also renders when `iter52_by_partner_date_present` is `false` because
 * that means the critical transaction index is missing and every
 * transactions page will time out until the migration runs. Same
 * yellow banner text so ops can react uniformly.
 */
export default function DeploySyncBanner() {
    const [state, setState] = useState({ marker: null, indexOk: null });
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let alive = true;
        const check = async () => {
            try {
                const r = await fetch("/api/health/version", { credentials: "include" });
                if (!r.ok) return;
                const j = await r.json();
                if (!alive) return;
                setState({
                    marker: j?.iter_marker || null,
                    indexOk: typeof j?.iter52_by_partner_date_present === "boolean"
                        ? j.iter52_by_partner_date_present
                        : null,
                });
            } catch { /* offline / legacy backend */ }
        };
        check();
        // Poll every 30 s so the banner disappears the moment the deploy
        // catches up — no user action needed.
        const t = setInterval(check, 30_000);
        return () => { alive = false; clearInterval(t); };
    }, []);

    if (dismissed) return null;
    const { marker, indexOk } = state;
    const beNum = iterNum(marker);
    const feNum = iterNum(EXPECTED_ITER_MARKER);

    // Case 1: backend behind frontend — backend deploy pending.
    const backendStale = !isNaN(beNum) && !isNaN(feNum) && beNum < feNum;
    // Case 2: frontend behind backend — user's browser has an old bundle.
    const frontendStale = !isNaN(beNum) && !isNaN(feNum) && beNum > feNum;
    // Case 3: critical index missing — behaves the same as backendStale
    // for the user (queries will time out).
    const indexMissing = indexOk === false;

    if (!backendStale && !frontendStale && !indexMissing) return null;

    // Force-reload ignoring HTTP cache (Ctrl+Shift+R equivalent).
    // Some browsers ignore reload(true) — the cache-buster search
    // param bypasses any lingering service-worker cache too.
    const hardReload = () => {
        const url = new URL(window.location.href);
        url.searchParams.set("_v", Date.now().toString());
        window.location.replace(url.toString());
    };

    const isFrontendCase = frontendStale;
    const bg = isFrontendCase
        ? "bg-amber-50 border-amber-300 text-amber-900"
        : "bg-yellow-50 border-yellow-300 text-yellow-900";
    const title = isFrontendCase
        ? "A new version is available"
        : indexMissing
            ? "Database indexes still building"
            : "Deploy in progress";
    const message = isFrontendCase
        ? `Your browser is running an older build (${EXPECTED_ITER_MARKER}) than the server (${marker}). Reload to pick up the latest fixes.`
        : indexMissing
            ? "The transaction indexes haven’t finished migrating yet. Some pages may show timeouts for the next minute or two."
            : `The backend is still catching up (running ${marker}, this build expects ${EXPECTED_ITER_MARKER}). Some pages may time out for ~1 minute.`;

    return (
        <div
            data-testid="deploy-sync-banner"
            className={`px-4 py-2 border-b flex items-center gap-3 text-sm ${bg}`}
        >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="font-medium">{title}</div>
                <div className="text-xs opacity-90 truncate">{message}</div>
            </div>
            {isFrontendCase && (
                <button
                    type="button"
                    onClick={hardReload}
                    data-testid="deploy-sync-reload-btn"
                    className="inline-flex items-center gap-1 px-3 py-1 rounded bg-white/70 hover:bg-white text-xs font-medium border border-amber-400"
                >
                    <RefreshCw className="h-3 w-3" />
                    Reload
                </button>
            )}
            <button
                type="button"
                onClick={() => setDismissed(true)}
                data-testid="deploy-sync-dismiss-btn"
                className="text-xs opacity-60 hover:opacity-100 px-2"
                aria-label="Dismiss"
            >
                ×
            </button>
        </div>
    );
}
